import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useIsAuthenticated } from '@azure/msal-react';
import { api } from '../lib/api';
import { clearSwrCacheByPrefix, hasFreshSwrCache, swrLoad } from '../lib/swrCache';
import { useIsAdmin } from '../lib/auth';
import { confirmDialog, toast } from '../lib/dialogs';
import { useEventImageUrl } from '../lib/image-sas';
import type { ExtractedEvent, Venue } from '../types';
import EventDetailModal from '../components/EventDetailModal';
import { useEventRejection } from '../hooks/useEventRejection';
import EventFiltersBar from '../components/EventFiltersBar';
import EntityFollowChip from '../components/EntityFollowChip';
import EventReactionButtons from '../components/EventReactionButtons';
import { useFavorites } from '../lib/favorites';
import { useTasteSettings } from '../lib/tasteSettings';
import { useTasteMatch } from '../lib/tasteMatch';
import MatchBadge from '../components/MatchBadge';
import EventReviewWarningChips from '../components/EventReviewWarningChips';
import VenueNeighborhoodEditor from '../components/VenueNeighborhoodEditor';
import { DateProofWarning, dateProofRangeOptions, nonTemporalWarnings, useDateProofEvents } from '../lib/dateProofReview';
import { compareEventDates, formatRange } from '../lib/eventDates';
import { visibleChipTags, formatTag } from '../lib/tagTaxonomy';
import SectionTabs from '../components/SectionTabs';
import {
  buildTasteProfile,
  scoreEvent,
  MIN_TASTE_FOR_SCORING,
  MIN_TASTE_SCORE_THRESHOLD
} from '../lib/tasteScore';
import { EVENTS_TABS } from '../lib/section-tabs';
import {
  type FilterState,
  applyFilters,
  DATE_WINDOW_ERROR,
  presetWindow,
  readFiltersFromUrl,
  writeFiltersToUrl,
} from '../lib/eventFilters';

// Fix Leaflet's default marker icons broken by Vite's asset bundling.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER: [number, number] = [30.3, -81.67];
const DEFAULT_ZOOM = 13;

interface VenueGroup {
  venueId: string;
  name: string;
  lat: number;
  lng: number;
  neighborhood?: string;
  /** True when an admin pinned this venue's neighborhood (skipped by the bulk sweep). */
  manualNeighborhoodOverride?: boolean;
  events: ExtractedEvent[];
}

/**
 * Marker tier — distinguishes a venue with **directly-favorited events**
 * (highest emphasis) from a venue that is itself favorited (mid emphasis)
 * from a regular venue (default blue numbered pin).
 */
type MarkerTier = 'favorite-event' | 'favorite-venue' | 'normal';

/** Build a numbered marker icon so the count of events at a venue is visible at a glance. */
function countIcon(count: number, tier: MarkerTier, favoriteEventCount: number): L.DivIcon {
  // Bigger badge for venues with more events; bump again for favorite-event tier.
  const baseSize = count >= 10 ? 38 : count >= 5 ? 34 : 30;
  const size = tier === 'favorite-event' ? baseSize + 6 : baseSize;
  const tierClass =
    tier === 'favorite-event' ? ' mh-map-marker-badge--favorite-event'
    : tier === 'favorite-venue' ? ' mh-map-marker-badge--favorite-venue'
    : '';
  // Saved-event marker (green): show the count of saved events at this venue.
  // No star here — a star now exclusively means "followed venue" (gold).
  // Favorite-venue marker: small gold star overlay on the count.
  // Normal: just the count.
  let inner: string;
  if (tier === 'favorite-event') {
    inner = String(favoriteEventCount);
  } else if (tier === 'favorite-venue') {
    inner = `<span class="mh-map-marker-badge__star mh-map-marker-badge__star--corner" aria-hidden="true">★</span>${count}`;
  } else {
    inner = String(count);
  }
  return L.divIcon({
    className: 'mh-map-marker',
    html: `<div class="mh-map-marker-badge${tierClass}" style="width:${size}px;height:${size}px;line-height:${size - 2}px;">${inner}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
  });
}

export default function EventsMapPage() {
  const [params, setParams] = useSearchParams();
  const [filters, setFiltersState] = useState<FilterState>(() => readFiltersFromUrl(params));
  const [tasteOnly, setTasteOnly] = useState<boolean>(() => params.get('taste') === '1');
  const [loadedEvents, setEvents] = useState<ExtractedEvent[] | null>(null);
  const events = useDateProofEvents(loadedEvents);
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExtractedEvent | null>(null);
  const isAdmin = useIsAdmin();
  const isAuthed = useIsAuthenticated();
  const { promptReject: openRejectModal, modal: rejectModal } = useEventRejection({
    onRejected: (event) => {
      setEvents((curr) => (curr ? curr.filter((e) => e.rowKey !== event.rowKey) : curr));
      setSelected((s) => (s && s.rowKey === event.rowKey ? null : s));
    },
  });
  const favorites = useFavorites();
  const tasteSettings = useTasteSettings();
  const resolveImage = useEventImageUrl();

  // Mirror HomePage: only the `taste` URL param is owned by this page; the
  // rest live inside FilterState. Helper so the toggle below stays terse.
  const extraParams = (): Record<string, string | undefined> => ({
    taste: tasteOnly ? '1' : undefined,
  });

  const setFilters = (updater: (f: FilterState) => FilterState) => {
    setFiltersState((prev) => {
      const next = updater(prev);
      writeFiltersToUrl(next, extraParams(), (p) => setParams(p, { replace: true }));
      return next;
    });
  };

  // Admin: decline a venue (mirrors HomePage.hideVenue). Optimistically
  // prunes events sharing the venueId and clears the public events SWR
  // cache so other windows refetch.
  async function hideVenue(venueId: string, venueName: string): Promise<void> {
    const label = venueName || 'this venue';
    if (!(await confirmDialog({
      title: 'Decline venue',
      body:
        `Decline "${label}"? All of its events will be hidden from the public list. ` +
        `You can re-approve it from the Manage venues page.`,
      confirmLabel: 'Decline',
      danger: true,
    }))) return;
    try {
      await api.patch(`/api/mgmt/venues/${encodeURIComponent(venueId)}`, {
        reviewStatus: 'declined',
        active: false,
        enrichmentStatus: 'skipped',
      });
      setEvents((curr) => curr ? curr.filter((e) => e.venueId !== venueId) : curr);
      clearSwrCacheByPrefix('events:');
      setSelected((s) => (s && s.venueId === venueId ? null : s));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not decline venue: ${msg}`);
    }
  }

  // Admin: hide a single event from the map view. The shared
  // `useEventRejection` hook owns the modal + API call; we just close any
  // open detail panel before opening the confirmation.
  function promptHideEvent(event: ExtractedEvent): void {
    setSelected((s) => (s && s.rowKey === event.rowKey ? null : s));
    openRejectModal(event);
  }

  // Load venues once for coordinates lookup (events sometimes lack lat/lng themselves).
  useEffect(() => {
    let cancelled = false;
    void swrLoad<Venue[]>({
      key: 'venues:public',
      fetcher: () => api.get<Venue[]>('/api/venues/public'),
      onData: (data) => setVenues(data),
      onError: () => setVenues([]),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const window = presetWindow(filters);
    if (!window) {
      setError(DATE_WINDOW_ERROR);
      setEvents([]);
      return;
    }
    setError(null);
    const { from, to } = window;
    const fromIso = from.toISOString();
    const toIso = to.toISOString();
    const includeHidden = filters.venueVisibility === 'all';
    const qs = `from=${fromIso}&to=${toIso}${includeHidden ? '&includeHidden=1' : ''}`;
    const key = `events:${fromIso}|${toIso}|h=${includeHidden ? 1 : 0}`;
    if (!hasFreshSwrCache(key)) setEvents(null);
    let cancelled = false;
    void swrLoad<ExtractedEvent[]>({
      key,
      fetcher: () => api.get<ExtractedEvent[]>(`/api/events/published?${qs}`),
      onData: (data) => setEvents(data),
      onError: (e) => setError(String(e)),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, [filters.preset, filters.customFrom, filters.customTo, filters.venueVisibility]);

  const venueCoords = useMemo(() => {
    const map = new Map<
      string,
      { lat: number; lng: number; name: string; neighborhood?: string; manualNeighborhoodOverride?: boolean }
    >();
    for (const v of venues ?? []) {
      if (v.lat != null && v.lng != null) {
        map.set(v.rowKey, {
          lat: v.lat,
          lng: v.lng,
          name: v.name,
          neighborhood: v.partitionKey,
          manualNeighborhoodOverride: v.manualNeighborhoodOverride,
        });
      }
    }
    return map;
  }, [venues]);

  // Admin neighborhood reassignments made from a venue bubble in this session.
  // The saved partition is authoritative on the server, but the events payload
  // is a snapshot that still carries the venue's old neighborhood, so we apply
  // the confirmed value on top until the next fetch replaces both.
  const [neighborhoodOverrides, setNeighborhoodOverrides] = useState<Record<string, string>>({});
  const handleNeighborhoodChanged = useCallback(
    (venueId: string, neighborhood: string) => {
      setNeighborhoodOverrides((curr) => ({ ...curr, [venueId]: neighborhood }));
    },
    [],
  );

  // Neighborhood names available to the admin dropdown — every partition the
  // venue catalog currently uses. `NeighborhoodSelect` adds free-text names.
  const neighborhoodOptions = useMemo(
    () => Array.from(new Set((venues ?? []).map((v) => v.partitionKey).filter(Boolean))).sort(),
    [venues],
  );

  // Server-supplied cosine similarity for the user's top tag-scored candidates
  // when "My taste only" is active. Empty by default; falls back to tag-only
  // scoring if the request fails or embeddings are missing.
  const [tasteEmbeddingScores, setTasteEmbeddingScores] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!isAuthed || !tasteOnly || !events || favorites.tasteIds.size < MIN_TASTE_FOR_SCORING) {
      setTasteEmbeddingScores(new Map());
      return;
    }
    const markedEvents = events.filter(e => favorites.tasteIds.has(e.rowKey));
    const profile = buildTasteProfile(markedEvents);
    if (profile.size < MIN_TASTE_FOR_SCORING) return;
    const candidates = events
      .filter(e => !profile.markedRowKeys.has(e.rowKey))
      .map(e => ({ id: e.rowKey, s: scoreEvent(e, profile, undefined, tasteSettings.weights).score }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 200)
      .map(x => x.id);
    if (candidates.length === 0) {
      setTasteEmbeddingScores(new Map());
      return;
    }
    let cancelled = false;
    api.post<{ scores: Record<string, number> }>(
      '/api/me/taste/embedding-scores',
      { candidateIds: candidates }
    )
      .then(res => {
        if (cancelled) return;
        const m = new Map<string, number>();
        for (const [k, v] of Object.entries(res.scores ?? {})) {
          if (typeof v === 'number') m.set(k, v);
        }
        setTasteEmbeddingScores(m);
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('embedding-scores fetch failed', err);
          setTasteEmbeddingScores(new Map());
        }
      });
    return () => { cancelled = true; };
  }, [isAuthed, tasteOnly, events, favorites.tasteIds, tasteSettings.weights]);

  const visible = useMemo(() => {
    if (!events) return [];
    const base = applyFilters(events, filters);
    if (tasteOnly && favorites.tasteIds.size >= MIN_TASTE_FOR_SCORING) {
      const markedEvents = events.filter(e => favorites.tasteIds.has(e.rowKey));
      const profile = buildTasteProfile(markedEvents);
      const scored = base
        .filter(e => !profile.markedRowKeys.has(e.rowKey))
        .filter(e => !favorites.dislikeIds.has(e.rowKey))
        .map(e => ({ e, score: scoreEvent(e, profile, tasteEmbeddingScores.get(e.rowKey), tasteSettings.weights).score }))
        .filter(x => x.score >= MIN_TASTE_SCORE_THRESHOLD)
        .sort((a, b) => b.score - a.score);
      return scored.map(x => x.e);
    }
    return base;
  }, [events, filters, tasteOnly, favorites.tasteIds, favorites.dislikeIds, tasteEmbeddingScores, tasteSettings.weights]);

  // Global taste-match scorer for the popup match badge. Registering the loaded
  // events keeps the profile fresh; embedding scores blend in when available.
  const tasteMatch = useTasteMatch();
  useEffect(() => {
    if (events && events.length > 0) tasteMatch.registerEvents(events);
  }, [events, tasteMatch]);
  const matchFor = useCallback(
    (e: ExtractedEvent) => tasteMatch.match(e, tasteEmbeddingScores.get(e.rowKey)),
    [tasteMatch, tasteEmbeddingScores]
  );

  // Group filtered events by venueId, dropping any without resolvable coordinates.
  const venueGroups = useMemo<VenueGroup[]>(() => {
    const groups = new Map<string, VenueGroup>();
    for (const ev of visible) {
      if (!ev.venueId) continue;
      const coords = venueCoords.get(ev.venueId);
      if (!coords) continue;
      const existing = groups.get(ev.venueId);
      if (existing) {
        existing.events.push(ev);
      } else {
        groups.set(ev.venueId, {
          venueId: ev.venueId,
          name: ev.venueName ?? coords.name,
          lat: coords.lat,
          lng: coords.lng,
          neighborhood: neighborhoodOverrides[ev.venueId] ?? ev.neighborhood ?? coords.neighborhood,
          manualNeighborhoodOverride: coords.manualNeighborhoodOverride,
          events: [ev],
        });
      }
    }
    // Sort each group's events chronologically.
    for (const g of groups.values()) {
      g.events.sort(compareEventDates);
    }
    return [...groups.values()].sort((a, b) => b.events.length - a.events.length);
  }, [visible, venueCoords, neighborhoodOverrides]);

  const eventsWithVenue = visible.filter((e) => e.venueId).length;
  const mappedEventCount = venueGroups.reduce((sum, g) => sum + g.events.length, 0);
  const unmappedCount = eventsWithVenue - mappedEventCount;

  return (
    <SectionTabs ariaLabel="Events sections" items={EVENTS_TABS}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="mb-0">Events map</h1>
        {isAuthed && (
          <button
            type="button"
            className={`btn btn-sm ${tasteOnly ? 'btn-info' : 'btn-outline-info'}`}
            onClick={() => {
              const next = !tasteOnly;
              setTasteOnly(next);
              writeFiltersToUrl(
                filters,
                { taste: next ? '1' : undefined },
                (p) => setParams(p, { replace: true })
              );
            }}
            aria-pressed={tasteOnly}
            disabled={favorites.tasteIds.size < MIN_TASTE_FOR_SCORING}
            title={
              favorites.tasteIds.size < MIN_TASTE_FOR_SCORING
                ? `Save at least ${MIN_TASTE_FOR_SCORING} events (👍) to unlock personalized recommendations`
                : 'Show events similar to the ones you’ve saved'
            }
          >
            <i className="bi bi-stars me-1" aria-hidden="true" /> Recommended
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <EventFiltersBar events={events} filters={filters} setFilters={setFilters} />

      <p className="small mb-2 mh-map-status" aria-live="polite">
        {events === null
          ? 'Loading events…'
          : `Showing ${mappedEventCount} event${mappedEventCount === 1 ? '' : 's'} across ${venueGroups.length} venue${venueGroups.length === 1 ? '' : 's'}` +
            (unmappedCount > 0
              ? ` (${unmappedCount} more match the filters but the venue has no coordinates yet)`
              : '') +
            '.'}
      </p>

      <div className="mh-map-container rounded border overflow-hidden">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {venueGroups.map((g) => {
            const favoriteEventCount = g.events.reduce(
              (n, e) => n + (favorites.eventIds.has(e.rowKey) ? 1 : 0),
              0,
            );
            const isFavoriteVenue = favorites.venueIds.has(g.venueId);
            const tier: MarkerTier =
              favoriteEventCount > 0 ? 'favorite-event'
              : isFavoriteVenue ? 'favorite-venue'
              : 'normal';
            return (
            <Marker
              key={g.venueId}
              position={[g.lat, g.lng]}
              icon={countIcon(g.events.length, tier, favoriteEventCount)}
              zIndexOffset={tier === 'favorite-event' ? 1000 : tier === 'favorite-venue' ? 500 : 0}
            >
              <Popup maxWidth={340}>
                <div className="mh-map-popup">
                  <div className="mh-map-popup__header">
                    <div className="mh-map-popup__venue">
                      <strong>
                        <Link to={`/venues/${g.venueId}`}>{g.name}</Link>
                      </strong>
                      {isAdmin ? (
                        <VenueNeighborhoodEditor
                          compact
                          venue={{
                            rowKey: g.venueId,
                            name: g.name,
                            partitionKey: g.neighborhood ?? '',
                            manualNeighborhoodOverride: g.manualNeighborhoodOverride,
                          }}
                          options={neighborhoodOptions}
                          onChanged={(_rowKey, next) => handleNeighborhoodChanged(g.venueId, next)}
                        />
                      ) : (
                        g.neighborhood && (
                          <div className="text-muted small">{g.neighborhood}</div>
                        )
                      )}
                    </div>
                    <div className="mh-map-popup__venue-actions">
                      <EntityFollowChip kind="venue" targetId={g.venueId} targetName={g.name} offLabel />
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-1 text-danger"
                          onClick={() => void hideVenue(g.venueId, g.name)}
                          aria-label={`Hide venue ${g.name}`}
                          title="Decline venue (hides all of its events)"
                        >
                          <i className="bi bi-eye-slash" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="small fw-semibold mb-1">
                    {g.events.length} event{g.events.length === 1 ? '' : 's'}
                  </div>
                  <ul className="list-unstyled mb-0 mh-map-popup__events">
                    {g.events.map((e) => {
                      const thumb = resolveImage(e.imageUrl);
                      const chips = visibleChipTags(e.tags, e).slice(0, 2);
                      const meta = [e.eventType, e.priceInfo].filter(Boolean) as string[];
                      const isSaved = favorites.isSaved(e.rowKey);
                      return (
                        <li
                          key={e.rowKey}
                          className={`mh-map-popup-event${isSaved ? ' mh-map-popup-event--saved' : ''}`}
                        >
                          {thumb && (
                            <img
                              src={thumb}
                              alt=""
                              loading="lazy"
                              className="mh-map-popup-thumb"
                            />
                          )}
                          <div className="mh-map-popup-event__body">
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-start fw-semibold mh-map-popup-event__title"
                              onClick={() => setSelected(e)}
                            >
                              {e.title}
                            </button>
                            <div className="text-muted small d-flex flex-column align-items-start gap-1">
                              <time dateTime={e.startUtc}>{formatRange(e, dateProofRangeOptions(e))}</time>
                              <DateProofWarning event={e} />
                            </div>
                            {e.dateFlaggedWrong && (
                              <span className="badge mh-wrong-date-chip">
                                <i className="bi bi-calendar-x me-1" aria-hidden="true" />Wrong date
                              </span>
                            )}
                            <MatchBadge match={matchFor(e)} />
                            <EventReviewWarningChips warnings={nonTemporalWarnings(e.reviewWarnings)} />
                            {(meta.length > 0 || e.recurringPattern) && (
                              <div className="text-muted small mh-map-popup-event__meta">
                                {e.recurringPattern && (
                                  <span title={`Recurring: ${e.recurringPattern}`} aria-label="Recurring event">🔁 </span>
                                )}
                                {meta.join(' · ')}
                              </div>
                            )}
                            {chips.length > 0 && (
                              <div className="mh-map-popup-event__chips">
                                {chips.map((t) => (
                                  <span key={t} className="badge text-bg-light border">
                                    {formatTag(t)}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="mh-map-popup-event__actions">
                              <EventReactionButtons event={e} variant="icon" />
                              {isAdmin && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-1 text-danger"
                                  onClick={() => promptHideEvent(e)}
                                  aria-label={`Hide event ${e.title}`}
                                  title="Hide event (removes from public list)"
                                >
                                  <i className="bi bi-x-circle" aria-hidden="true" />
                                </button>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Popup>
            </Marker>
            );
          })}
        </MapContainer>
      </div>

      <EventDetailModal
        event={selected}
        onClose={() => setSelected(null)}
        onReject={isAdmin ? (e) => promptHideEvent(e) : undefined}
        onHideVenue={isAdmin ? (e) => {
          if (e.venueId) void hideVenue(e.venueId, e.venueName ?? e.locationText ?? '');
        } : undefined}
        onEventUpdated={isAdmin ? (updated) => {
          setEvents((curr) => curr ? curr.map((x) => x.rowKey === updated.rowKey ? { ...x, ...updated } : x) : curr);
          setSelected((curr) => curr && curr.rowKey === updated.rowKey ? { ...curr, ...updated } : curr);
        } : undefined}
      />
      {rejectModal}
    </SectionTabs>
  );
}
